import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import FeatherIcon from "feather-icons-react";
import FormHandler from "../shared/utils/FormHandler";
import useUserApi from "../apis/userApi";
import { validateCreateUser, validateEditUser } from "../utils/FormValidationRules";
import { isSuper } from "../shared/utils/SharedAuthentication";

const Users = () => {
  const { users, createUser, editUser } = useUserApi();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [submitErrors, setSubmitErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { values, errors, handleChange, handleOnBlur, initForm } = FormHandler(
    () => {},
    isUpdate ? validateEditUser : validateCreateUser
  );

  function resetFormData() {
    setIsModalVisible(false);
    initForm({});
    setSubmitErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  }

  const handleAddClick = () => {
    setIsUpdate(false);
    setSubmitErrors({});
    initForm({ role: "user" });
    setIsModalVisible(true);
  };

  const handleEditClick = (user) => {
    setIsUpdate(true);
    setSubmitErrors({});
    initForm({
      id: user.id,
      username: user.username,
      role: user.role || "user",
    });
    setIsModalVisible(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const validate = isUpdate ? validateEditUser : validateCreateUser;
    const formErrors = validate(values);
    setSubmitErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    if (isUpdate) {
      editUser(
        {
          id: values.id,
          username: values.username,
          role: values.role || "user",
          ...(values.password ? { password: values.password } : {}),
        },
        resetFormData
      );
    } else {
      createUser(
        {
          username: values.username,
          password: values.password,
          role: values.role || "user",
        },
        resetFormData
      );
    }
  };

  if (!isSuper()) {
    return (
      <div className="users-page">
        <div className="innovation-error">
          <span className="error-icon">⚠️</span>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="innovation-title">User Management</h2>
          <button onClick={handleAddClick} className="sa-table-btn-secondary" style={{ width: "200px" }}>
            <FeatherIcon icon={"plus"} className={"sa-table-icon-size"} />{" "}
            <span className={"sa-table-icon"}>CREATE NEW USER</span>
          </button>
        </div>

        <div className="sa-table-container">
          <table className="table table-borderless sa-table-width">
            <thead>
              <tr>
                <th className={"sa-table-head-sticky"}>Username</th>
                <th className={"sa-table-head-sticky"}>Role</th>
                <th className="sa-table-item-align sa-table-head-sticky"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="3" className={"sa-table-data"}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id || index}>
                    <td className={"sa-table-data"}>{user.username}</td>
                    <td className={"sa-table-data"}>{user.role}</td>
                    <td className="sa-table-item-align">
                      <div className={"sa-table-action-flex"}>
                        <div
                          className="sa-table-actions sa-action-m"
                          data-toggle="tooltip"
                          title="Edit"
                        >
                          <FeatherIcon
                            onClick={() => handleEditClick(user)}
                            className="table-action"
                            icon={"edit"}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <div className={"sa-popup-bg " + (!isModalVisible && "hide")}>
        <div className="sa-popup">
          <form onSubmit={handleFormSubmit} className={"sa-modal-box-style"}>
            <div className="sa-popup-header">
              <span className={"sa-model-heading"}>
                {!isUpdate ? "CREATE USER" : "EDIT USER"}
              </span>
              <div className="sa-popup-close-icon" onClick={resetFormData}>
                <FeatherIcon className={"sa-modal-close-icon"} icon={"x"} />
              </div>
            </div>
            <div className="sa-popup-content">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    name="username"
                    value={values.username || ""}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    className={
                      errors.username || submitErrors.username
                        ? "form-control warning-input"
                        : "form-control"
                    }
                  />
                  {(errors.username || submitErrors.username) && (
                    <p className="warning-input-msg">
                      {errors.username || submitErrors.username}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    name="role"
                    value={values.role || "user"}
                    onChange={handleChange}
                    className={"form-control"}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="password">
                    {isUpdate ? "New Password (leave blank to keep current)" : "Password"}
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={values.password || ""}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    className={
                      errors.password || submitErrors.password
                        ? "form-control warning-input"
                        : "form-control"
                    }
                  />
                  <span className="field-icon" onClick={() => setShowPassword(!showPassword)}>
                    <FeatherIcon className="toggle-password" icon={showPassword ? "eye" : "eye-off"} />
                  </span>
                  {(errors.password || submitErrors.password) && (
                    <p className="warning-input-msg">
                      {errors.password || submitErrors.password}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={values.confirmPassword || ""}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    className={
                      errors.confirmPassword || submitErrors.confirmPassword
                        ? "form-control warning-input"
                        : "form-control"
                    }
                  />
                  <span
                    className="field-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <FeatherIcon
                      className="toggle-password"
                      icon={showConfirmPassword ? "eye" : "eye-off"}
                    />
                  </span>
                  {(errors.confirmPassword || submitErrors.confirmPassword) && (
                    <p className="warning-input-msg">
                      {errors.confirmPassword || submitErrors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="sa-popup-btn">
              <button
                type={"button"}
                className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
                onClick={resetFormData}
              >
                CANCEL
              </button>
              <button type="submit" className="sa-popup-secondary-btn-style">
                {!isUpdate ? "CREATE" : "UPDATE"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Users;
